import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';

import { ApiForm, Field, Submit, TextInput, useApiForm } from '../../shared/form';
import { Button } from '../../shared/ui/button';
import { FactList, FactRow } from '../../shared/ui/fact-list';
import { DataPanel } from '../../shared/ui/layout';
import { Input } from '../../shared/ui/input';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import {
  acceptInvitation,
  acceptInvitationSchema,
  lookupInvitation,
  type TAcceptInvitation,
  type TInvitationPreview,
} from '../../entities/session';
import { shortDate } from '../../shared/lib/format';
import { AuthScreen } from './ui/auth-screen';

// `invitedByName` is a string the generated schema types as `Record<string, never> | null`.
const asText = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const Expired = ({ invitation }: { invitation: TInvitationPreview }) => (
  <AuthScreen
    title="This invitation has expired"
    brand={false}
    description={`It ran out on ${shortDate(invitation.expiresAt)}. You need a new one.`}
  >
    <DataPanel className="mt-5">
      <FactList ruled={false}>
        <FactRow label="Organization">{invitation.organizationName}</FactRow>
        {asText(invitation.invitedByName) && (
          <FactRow label="Invited by">{asText(invitation.invitedByName)}</FactRow>
        )}
      </FactList>
    </DataPanel>

    <p className="mt-4 text-meta text-muted">Ask for a new one, to the same address</p>

    <div className="mt-section">
      <Link to="/sign-in">
        <Button variant="ghost" block>
          Back to sign in
        </Button>
      </Link>
    </div>
  </AuthScreen>
);

const Accept = ({ token, invitation }: { token: string; invitation: TInvitationPreview }) => {
  const navigate = useNavigate();
  const invitedBy = asText(invitation.invitedByName);

  const api = useApiForm<TAcceptInvitation>({
    schema: acceptInvitationSchema,
    defaultValues: { name: '', password: '' },
    onSubmit: async (values) => {
      await acceptInvitation({ token, ...values });
      await navigate({ to: '/flows' });
    },
  });

  return (
    <AuthScreen
      title={`Join ${invitation.organizationName}`}
      brand={false}
      description={
        invitedBy
          ? `${invitedBy} invites you to their organization in Hookpipe.`
          : 'You have been invited to an organization in Hookpipe.'
      }
    >
      <DataPanel className="mt-5">
        <FactList ruled={false}>
          <FactRow label="Organization">{invitation.organizationName}</FactRow>
          <FactRow label="Role">{invitation.roleDescription}</FactRow>
          <FactRow label="Expires" mono>
            {shortDate(invitation.expiresAt)}
          </FactRow>
        </FactList>
      </DataPanel>

      <ApiForm api={api}>
        <div className="flex flex-col">
          <label htmlFor="invite-email" className="mb-label text-meta font-medium text-muted">
            Email
          </label>
          <Input id="invite-email" value={invitation.emailMasked} disabled readOnly />
          <p className="mt-label text-meta text-muted">The invitation is tied to this address</p>
        </div>

        <Field<TAcceptInvitation> name="name" label="Your name">
          <TextInput autoComplete="name" placeholder="Dasha" />
        </Field>

        <Field<TAcceptInvitation> name="password" label="Choose a password">
          <TextInput
            type="password"
            autoComplete="new-password"
            placeholder="8+ characters, a letter and a number"
          />
        </Field>

        <Submit waitingLabel="Joining">Accept invitation</Submit>
      </ApiForm>
    </AuthScreen>
  );
};

export const InvitationPage = ({ token }: { token: string }) => {
  const query = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => lookupInvitation(token),
  });

  if (query.isPending) {
    return (
      <AuthScreen title="Opening the invitation" brand={false}>
        <div className="mt-section">
          <LoadingState rows={3} />
        </div>
      </AuthScreen>
    );
  }

  if (query.isError) {
    return (
      <AuthScreen title="This link did not work" brand={false}>
        <div className="mt-section">
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </div>
      </AuthScreen>
    );
  }

  return query.data.status === 'pending' ? (
    <Accept token={token} invitation={query.data} />
  ) : (
    <Expired invitation={query.data} />
  );
};
